import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DateUtils } from '@pbt/pbt-ui-components'

import type { Appointment } from '~/api/graphql/generated/types'
import PatientHistoryEmptyState from '~/components/common/PatientHistoryEmptyState'
import PatientHistoryWidget, {
  type TabData,
} from '~/components/common/PatientHistoryWidget'
import i18n from '~/locales/i18n'

import AppointmentWidgetReasonCell from './AppointmentWidgetReasonCell'
import AppointmentWidgetTypeCell from './AppointmentWidgetTypeCell'
import {
  AppointmentsTab,
  type AppointmentsTabType,
  useAppointmentsSubscription,
} from './useAppointmentsSubscription'

import styles from './AppointmentsSummaryWidget.module.scss'

const LIMIT = 10

const appointmentsColumns = () => [
  {
    id: 'date',
    prop: (item: Appointment) =>
      DateUtils.formatDate(item.scheduledStartDatetime, false, 'MM/DD/YY'),
    label: i18n.t('Common:DATE_TIME'),
  },
  {
    id: 'appointmentType',
    component: (item: Appointment) => <AppointmentWidgetTypeCell item={item} />,
    label: i18n.t('Common:APPOINTMENT_TYPE'),
  },
  {
    id: 'reason',
    component: (item: Appointment) => (
      <AppointmentWidgetReasonCell item={item} />
    ),
    label: i18n.t('Common:REASON_FOR_VISIT'),
  },
]

interface AppointmentsSummaryWidgetProps {
  patientId: string
}

const AppointmentsSummaryWidget = ({
  patientId,
}: AppointmentsSummaryWidgetProps) => {
  const { t } = useTranslation(['Clients', 'Common'])
  const [selectedTab, setSelectedTab] = useState<AppointmentsTabType>(
    AppointmentsTab.PREVIOUS_TAB_ID,
  )
  const [currentPage, setCurrentPage] = useState(1)

  const offset = (currentPage - 1) * LIMIT

  const {
    previousAppointments,
    scheduledAppointments,
    loading: isLoading,
    previousAppointmentsCount,
    scheduledAppointmentsCount,
  } = useAppointmentsSubscription(patientId, offset, LIMIT, selectedTab)

  const columns = useMemo(() => appointmentsColumns(), [])

  const tabs: TabData<Appointment>[] = [
    {
      id: AppointmentsTab.PREVIOUS_TAB_ID,
      label: t('Common:PREVIOUS'),
      labelCount: previousAppointmentsCount,
      data: previousAppointments,
      columns,
      totalCount: previousAppointmentsCount,
      noRecords: (
        <PatientHistoryEmptyState
          message={t('Clients:APPOINTMENTS_WIDGET.NO_PREVIOUS_APPOINTMENTS')}
        />
      ),
    },
    {
      id: AppointmentsTab.SCHEDULED_TAB_ID,
      label: t('Common:SCHEDULED'),
      labelCount: scheduledAppointmentsCount,
      data: scheduledAppointments,
      columns,
      totalCount: scheduledAppointmentsCount,
      noRecords: (
        <PatientHistoryEmptyState
          message={t('Clients:APPOINTMENTS_WIDGET.NO_SCHEDULED_APPOINTMENTS')}
        />
      ),
    },
  ]

  const handleTabChange = (tabId: number) => {
    setCurrentPage(1)
    setSelectedTab(tabId as AppointmentsTabType)
  }

  const handleScheduleNew = () => {
    window.open('/scheduler', '_blank')
  }

  return (
    <PatientHistoryWidget
      className={styles.appointmentsWidget}
      currentPage={currentPage}
      headerAction={{
        text: t('Common:SCHEDULE_NEW'),
        onClick: handleScheduleNew,
      }}
      isLoading={isLoading}
      layoutOptions={{
        flushRows: true,
      }}
      maxRows={LIMIT}
      selectedTab={selectedTab}
      tabs={tabs}
      title={t('Clients:APPOINTMENTS_WIDGET.TITLE')}
      onPageChange={setCurrentPage}
      onSelectedTabChange={handleTabChange}
    />
  )
}

export default AppointmentsSummaryWidget
